import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { CustomerListItem } from "@/components/Organisms/CustomersTable/CustomersTable.types";
import type { CustomerFilter } from "@/components/Templates/Customers/Customers.template.types";
import { useCustomersList } from "@/hooks/useCustomersList";
import { formatPersianNumber } from "@/lib/format/persian";

const PER_PAGE = 10;

function filterCustomers(customers: CustomerListItem[], filter: CustomerFilter): CustomerListItem[] {
  if (filter === "repeat") {
    return customers.filter((customer) => (customer.orders_count ?? 0) > 1);
  }
  if (filter === "new") {
    const now = new Date();
    return customers.filter((customer) => {
      const created = new Date(customer.created_at);
      return (
        created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear()
      );
    });
  }
  return customers;
}

export function useCustomersPage() {
  const { t } = useTranslation("customers");
  const [customerFilter, setCustomerFilter] = useState<CustomerFilter>("all");
  const customersList = useCustomersList();

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      customersList.setSearch(event.target.value);
    },
    [customersList],
  );

  const handleCloseDrawer = useCallback(() => {
    customersList.setSelectedCustomerId(null);
  }, [customersList]);

  const displayCustomers = useMemo(
    () => filterCustomers(customersList.customers, customerFilter),
    [customersList.customers, customerFilter],
  );

  const paginationLabel = useMemo(() => {
    if (!customersList.meta) return "";
    const from = (customersList.page - 1) * PER_PAGE + 1;
    const to = Math.min(customersList.page * PER_PAGE, customersList.meta.total);
    return t("pagination.showing", {
      from: formatPersianNumber(from),
      to: formatPersianNumber(to),
      total: formatPersianNumber(customersList.meta.total),
    });
  }, [customersList.meta, customersList.page, t]);

  return {
    ...customersList,
    customerFilter,
    setCustomerFilter,
    displayCustomers,
    handleSearchChange,
    handleCloseDrawer,
    paginationLabel,
  };
}
